import { supabase } from './supabaseService';
import type { ActivityAttachment } from '../types';

const BUCKET_NAME = 'activity-attachments';
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'text/csv'
];

class ActivityAttachmentsService {
  validateFile(file: File): { valid: boolean; error?: string } {
    if (file.size > MAX_FILE_SIZE) {
      return {
        valid: false,
        error: `El archivo es demasiado grande. Máximo ${MAX_FILE_SIZE / 1024 / 1024}MB`
      };
    }

    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      return {
        valid: false,
        error: 'Tipo de archivo no permitido'
      };
    }

    return { valid: true };
  }

  private generateFilePath(activityId: string, fileName: string): string {
    const timestamp = Date.now();
    const sanitizedName = fileName.replace(/[^a-zA-Z0-9.-]/g, '_');
    return `${activityId}/${timestamp}_${sanitizedName}`;
  }

  async uploadFile(
    activityId: string,
    file: File,
    uploadedBy: string,
    onProgress?: (progress: number) => void
  ): Promise<ActivityAttachment> {
    try {
      const validation = this.validateFile(file);
      if (!validation.valid) {
        throw new Error(validation.error);
      }

      const filePath = this.generateFilePath(activityId, file.name);

      console.log(`📤 Subiendo archivo: ${file.name} (${file.size} bytes)`);

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from(BUCKET_NAME)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('❌ Error subiendo archivo a Storage:', uploadError);
        throw new Error(`Error subiendo archivo: ${uploadError.message}`);
      }

      console.log('✅ Archivo subido a Storage:', uploadData.path);

      const attachmentData = {
        activity_id: activityId,
        file_name: file.name,
        file_size: file.size,
        file_type: file.type,
        file_path: uploadData.path,
        uploaded_by: uploadedBy
      };

      const { data: attachment, error: dbError } = await supabase
        .from('activity_attachments')
        .insert(attachmentData)
        .select()
        .single();

      if (dbError) {
        console.error('❌ Error guardando metadata en DB:', dbError);
        await supabase.storage.from(BUCKET_NAME).remove([uploadData.path]);
        throw new Error(`Error guardando metadata: ${dbError.message}`);
      }

      console.log('✅ Metadata guardada en DB:', attachment.id);

      return this.transformAttachment(attachment);
    } catch (error) {
      console.error('❌ Error en uploadFile:', error);
      throw error;
    }
  }

  async uploadMultipleFiles(
    activityId: string,
    files: File[],
    uploadedBy: string,
    onProgress?: (fileIndex: number, progress: number) => void
  ): Promise<ActivityAttachment[]> {
    const attachments: ActivityAttachment[] = [];

    for (let i = 0; i < files.length; i++) {
      try {
        const attachment = await this.uploadFile(
          activityId,
          files[i],
          uploadedBy,
          (progress) => onProgress?.(i, progress)
        );
        attachments.push(attachment);
      } catch (error) {
        console.error(`❌ Error subiendo archivo ${i + 1}:`, error);
        throw error;
      }
    }

    return attachments;
  }

  async getAttachmentsByActivity(activityId: string): Promise<ActivityAttachment[]> {
    try {
      console.log(`🔄 Cargando adjuntos para actividad: ${activityId}`);

      const { data, error } = await supabase
        .from('activity_attachments')
        .select('*')
        .eq('activity_id', activityId)
        .order('uploaded_at', { ascending: false });

      if (error) {
        console.error('❌ Error cargando adjuntos:', error);
        throw new Error(`Error cargando adjuntos: ${error.message}`);
      }

      console.log(`✅ ${data?.length || 0} adjuntos cargados`);

      return (data || []).map(att => this.transformAttachment(att));
    } catch (error) {
      console.error('❌ Error en getAttachmentsByActivity:', error);
      return [];
    }
  }

  async getDownloadUrl(filePath: string): Promise<string> {
    try {
      const { data, error } = await supabase.storage
        .from(BUCKET_NAME)
        .createSignedUrl(filePath, 3600);

      if (error) {
        console.error('❌ Error generando URL de descarga:', error);
        throw new Error(`Error generando URL: ${error.message}`);
      }

      return data.signedUrl;
    } catch (error) {
      console.error('❌ Error en getDownloadUrl:', error);
      throw error;
    }
  }

  async deleteAttachment(attachmentId: string): Promise<void> {
    try {
      console.log(`🗑️ Eliminando adjunto: ${attachmentId}`);

      const { data: attachment, error: fetchError } = await supabase
        .from('activity_attachments')
        .select('file_path')
        .eq('id', attachmentId)
        .single();

      if (fetchError) {
        throw new Error(`Error obteniendo adjunto: ${fetchError.message}`);
      }

      if (!attachment) {
        throw new Error('Adjunto no encontrado');
      }

      const { error: storageError } = await supabase.storage
        .from(BUCKET_NAME)
        .remove([attachment.file_path]);

      if (storageError) {
        console.warn('⚠️ Error eliminando archivo de Storage:', storageError);
      }

      const { error: dbError } = await supabase
        .from('activity_attachments')
        .delete()
        .eq('id', attachmentId);

      if (dbError) {
        throw new Error(`Error eliminando metadata: ${dbError.message}`);
      }

      console.log('✅ Adjunto eliminado correctamente');
    } catch (error) {
      console.error('❌ Error en deleteAttachment:', error);
      throw error;
    }
  }

  async deleteAttachmentsByActivity(activityId: string): Promise<void> {
    try {
      console.log(`🗑️ Eliminando todos los adjuntos de actividad: ${activityId}`);

      const { data: attachments, error: fetchError } = await supabase
        .from('activity_attachments')
        .select('file_path')
        .eq('activity_id', activityId);

      if (fetchError) {
        throw new Error(`Error obteniendo adjuntos: ${fetchError.message}`);
      }

      if (attachments && attachments.length > 0) {
        const filePaths = attachments.map(att => att.file_path);

        const { error: storageError } = await supabase.storage
          .from(BUCKET_NAME)
          .remove(filePaths);

        if (storageError) {
          console.warn('⚠️ Error eliminando archivos de Storage:', storageError);
        }
      }

      const { error: dbError } = await supabase
        .from('activity_attachments')
        .delete()
        .eq('activity_id', activityId);

      if (dbError) {
        throw new Error(`Error eliminando metadata: ${dbError.message}`);
      }

      console.log(`✅ ${attachments?.length || 0} adjuntos eliminados`);
    } catch (error) {
      console.error('❌ Error en deleteAttachmentsByActivity:', error);
      throw error;
    }
  }

  async getAttachmentCount(activityId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('activity_attachments')
        .select('*', { count: 'exact', head: true })
        .eq('activity_id', activityId);

      if (error) {
        console.error('❌ Error contando adjuntos:', error);
        return 0;
      }

      return count || 0;
    } catch (error) {
      console.error('❌ Error en getAttachmentCount:', error);
      return 0;
    }
  }

  isImageFile(fileType: string): boolean {
    return fileType.startsWith('image/');
  }

  isPdfFile(fileType: string): boolean {
    return fileType === 'application/pdf';
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  private transformAttachment(data: any): ActivityAttachment {
    return {
      id: data.id,
      activityId: data.activity_id,
      fileName: data.file_name,
      fileSize: data.file_size,
      fileType: data.file_type,
      filePath: data.file_path,
      uploadedBy: data.uploaded_by,
      uploadedAt: new Date(data.uploaded_at),
      createdAt: new Date(data.created_at)
    };
  }
}

export const activityAttachmentsService = new ActivityAttachmentsService();
